/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type CSSProperties, type Key, useCallback, useMemo, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';

import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { useMotion } from '@atlaskit/motion/entering/use-motion';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import type {
	contentHeightWhenFixed,
	contentInsetBlockStart,
	sideNavLiveWidthVar,
} from '@atlaskit/navigation-system/layout/layout-sizing';
import {
	PanelSplitter,
	PanelSplitterProvider,
	type ResizeBounds,
} from '@atlaskit/navigation-system/layout/panel-splitter';
import { useSideNavRef } from '@atlaskit/navigation-system/layout/side-nav';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { localPanelSplitterId } from './constants';
import { PanelSlot as ChatLayoutPanelSlot } from './panel-slot-chat-panel-layout';

const localPanelVar = '--p_lclPnlW';

const panelSplitterResizingVar = '--p_lclPnlRsz';

/* eslint-disable @compiled/shorthand-property-sorting -- The panel content motion token is an animation shorthand; panel opening intentionally overrides only its delay. */
const styles = cssMap({
	panelWrapper: {
		position: 'sticky',
		backgroundColor: token('elevation.surface.overlay'),
		height:
			'calc(100vh - var(--n_bnrM, 0px) - var(--n_tNvM, 0px))' satisfies typeof contentHeightWhenFixed,
		insetBlockStart: '0px',

		justifySelf: 'end',
		gridArea: 'main',
		/**
		 * For mobile viewports, the panel will try to take the minimum width, but no larger than 90% of the screen width.
		 *
		 * The minimum width is derived from the default width of the panel.
		 *
		 * This ensures the panel:
		 *
		 * - only shrinks below its minimum width if the viewport is too small
		 * - is not forced to grow if it is smaller than `365px`
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		width: `min(90%, var(--minWidth))`,
		'@media (min-width: 48rem)': {
			// Styles do not apply without the nested selector.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
			'&&': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
				width: `var(${panelSplitterResizingVar}, var(${localPanelVar}))`,
			},
		},
		'@media (min-width: 64rem)': {
			// This sets the sticky point to be just below top bar + banner. It's needed to ensure the stick
			// point is exactly where this element is rendered to with no wiggle room. Unfortunately the CSS
			// spec for sticky doesn't support "stick to where I'm initially rendered" so we need to tell it.
			insetBlockStart:
				'calc(var(--n_bnrM, 0px) + var(--n_tNvM, 0px))' satisfies typeof contentInsetBlockStart,
			backgroundColor: token('elevation.surface'),
		},
		'@media (min-width: 90rem)': {
			gridArea: 'panel',
		},
	},
	panelScrollContainer: {
		overflow: 'auto',
		height: '100%',
	},
	border: {
		// We use a shadow when the panel is an overlay
		boxShadow: token('elevation.shadow.overlay'),
		// Not required, but declaring explicitly because we really don't want a border at small sizes
		// Previously we had a transparent border to maintain width, but this unintentionally acted as padding
		borderInlineStart: 'none',
		'@media (min-width: 64rem)': {
			// Hiding the box shadow because we are adding a border
			boxShadow: 'initial',
			// We only want the border to be visible when it is not an overlay
			borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		},
	},
	entering: {
		'@media (prefers-reduced-motion: no-preference)': {
			animation: token('motion.panel.enter.right'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			"[dir='rtl'] &": {
				animation: token('motion.panel.enter.left'),
			},
			transitionProperty: 'position',
			transitionDuration: token('motion.duration.instant'),
			transitionDelay: '24ms', // Snaps main content when 60% of the panel has entered
			transitionBehavior: 'allow-discrete',
			'@starting-style': {
				position: 'fixed',
			},
		},
	},
	exiting: {
		'@media (prefers-reduced-motion: no-preference)': {
			animation: token('motion.panel.exit.right'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			"[dir='rtl'] &": {
				animation: token('motion.panel.exit.left'),
			},
			transitionProperty: 'position',
			transitionDuration: token('motion.duration.instant'),
			transitionDelay: '19ms', // Snaps main content when 60% of the panel has exited
			transitionBehavior: 'allow-discrete',
			position: 'fixed',
		},
	},
	contentEntering: {
		animation: token('motion.panel.content.enter'),
	},
	contentExiting: {
		animation: token('motion.panel.content.exit'),
	},
	contentChangeContainer: {
		height: '100%',
	},
});

const contentEnteringDelayStyles = cssMap({
	root: {
		animationDelay: token('motion.duration.xshort'),
	},
});
/* eslint-enable @compiled/shorthand-property-sorting */

const fallbackDefaultWidth = 365;
/**
 * We typically use the `defaultWidth` as the minimum resizing width,
 * but for large default widths we fallback to a standard value.
 *
 * This standard value of `400px` is to align with the Global Preview Panels.
 */
const fallbackResizeMinWidth = 400;

type PanelProps = {
	/**
	 * The content of the layout area.
	 */
	children: React.ReactNode;
	/**
	 * The accessible name of the slot, announced by screen readers.
	 */
	label?: string;
	/**
	 * The initial layout width used to derive the minimum resizing width. Defaults to 365px.
	 *
	 * The minimum is `Math.min(defaultWidth, 400)`. The maximum resizing width is half the viewport
	 * width after subtracting the sidebar width. The `width` prop supplies the requested width.
	 */
	defaultWidth?: number;
	/**
	 * The requested width of the panel, constrained by CSS when rendered.
	 */
	width: number;
	/**
	 * Called with the requested width after a pointer resize or each keyboard resize step.
	 */
	onWidthChange: (panelWidth: number) => void;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * Called when the user finishes a pointer or keyboard resize interaction.
	 *
	 * @param event The splitter's initial and final widths, in pixels.
	 */
	onResizeEnd?: (event: { initialWidth: number; finalWidth: number }) => void;
	/**
	 * Identifies the content currently displayed in the panel.
	 * @internal
	 */
	contentKey?: Key;
};

const PanelContentMotion = ({ children }: { children: React.ReactNode }): JSX.Element => {
	const { state, ref } = useMotion<HTMLDivElement>();

	return (
		<div
			css={[
				styles.contentChangeContainer,
				state === 'entering' && styles.contentEntering,
				state === 'exiting' && styles.contentExiting,
			]}
			ref={ref}
		>
			{children}
		</div>
	);
};

const LegacyPanelSlot = ({
	children,
	contentKey,
	defaultWidth = fallbackDefaultWidth,
	width,
	label = 'Panel',
	testId,
	onWidthChange,
	onResizeEnd,
}: PanelProps): JSX.Element => {
	const ref = useRef<HTMLDivElement | null>(null);
	const sideNavRef = useSideNavRef();

	// useMotion is applied only when the `platform-dst-motion-uplift-panel` gate is enabled.
	const { state, ref: motionRef } = useMotion<HTMLElement>();
	const isMotionUpliftEnabled = fg('platform-dst-motion-uplift-panel');

	// `mergeRefs` returns a new callback ref on every call, and combined with a new array literal
	// this would cause React to detach/re-attach the ref on every render. Memoise it so the merged
	// ref is stable across renders.
	const mergedRef = useMemo(
		() => (isMotionUpliftEnabled ? mergeRefs([ref, motionRef]) : ref),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isMotionUpliftEnabled, motionRef],
	);

	/**
	 * The minimum width that the panel can be resized to by the user.
	 *
	 * We only use the `defaultWidth` up to a point to avoid huge panels.
	 */
	const minWidth = Math.min(defaultWidth, fallbackResizeMinWidth);

	/**
	 * Returns the bounds for resizing, evaluated lazily when needed.
	 *
	 * Defined separately to the slot bounds because the resizing bounds need to be resolvable to a pixel value,
	 * and the panel's slot bounds use a complex CSS expression.
	 */
	const getResizeBounds = useCallback((): ResizeBounds => {
		const sideNavWidth = sideNavRef.current?.offsetWidth ?? 0;

		/**
		 * The panel should not resize larger than the page content, equivalent to the `Main` + `Aside` slots.
		 *
		 * This maximum width is equivalent to half the viewport width, after removing the sidebar width.
		 */
		const maxWidth = Math.round((window.innerWidth - sideNavWidth) / 2);

		return { min: `${minWidth}px`, max: `${maxWidth}px` };
	}, [minWidth, sideNavRef]);

	const panelWidthSlotBounds = {
		min: `${minWidth}px`,
		// `sideNavLiveWidthVar` is not defined if the `SideNav` is not mounted, so we fallback to `0px`.
		max: `round(nearest, calc((100vw - var(${
			'--n_sNvlw' satisfies typeof sideNavLiveWidthVar
		}, 0px)) / 2), 1px)`,
	};

	const panelVariableWidth = `clamp(${panelWidthSlotBounds.min}, ${width}px, ${panelWidthSlotBounds.max})`;

	return (
		<section
			aria-label={label}
			css={[
				isMotionUpliftEnabled && state === 'entering' && styles.entering,
				isMotionUpliftEnabled && state === 'exiting' && styles.exiting,
				styles.panelWrapper,
				styles.border,
			]}
			data-testid={testId}
			style={
				{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
					[localPanelVar]: panelVariableWidth,
					'--minWidth': `${minWidth}px`,
				} as CSSProperties
			}
			ref={mergedRef}
		>
			<PanelSplitterProvider
				panelId={localPanelSplitterId}
				panelRef={ref}
				panelWidth={width}
				onCompleteResize={onWidthChange}
				getResizeBounds={getResizeBounds}
				resizingCssVar={panelSplitterResizingVar}
				position="start"
			>
				{/**
				 * Overflow scroll styles are added here rather than on the `section` container element, so that the panel splitter
				 * component can overflow out of the `Panel` container, to increase the interactive grab area
				 */}
				<div
					css={[
						styles.panelScrollContainer,
						state === 'entering' && styles.contentEntering,
						isMotionUpliftEnabled && state === 'entering' && contentEnteringDelayStyles.root,
						state === 'exiting' && styles.contentExiting,
					]}
				>
					{isMotionUpliftEnabled && contentKey !== undefined ? (
						<ExitingPersistence exitThenEnter>
							<PanelContentMotion key={contentKey}>{children}</PanelContentMotion>
						</ExitingPersistence>
					) : (
						children
					)}
					<PanelSplitter
						label="Resize panel"
						onResizeEnd={onResizeEnd}
						testId={testId && `${testId}--splitter`}
					/>
				</div>
			</PanelSplitterProvider>
		</section>
	);
};

/**
 * Preserve existing panel behavior until the new layout is enabled.
 */
export const PanelSlot = ({
	children,
	contentKey,
	defaultWidth,
	compactDefaultWidth,
	minWidth,
	maxWidth,
	initialInlineWidth,
	width,
	label,
	testId,
	onWidthChange,
	onResizeEnd,
	onInlineResizeEnd,
	layoutRef,
	managedSizing,
	getManagedResizeBounds,
	onManagedResizeStart,
	onManagedResize,
	onManagedResizeEnd,
}: React.ComponentProps<typeof ChatLayoutPanelSlot> &
	Omit<Partial<PanelProps>, 'onResizeEnd'>): JSX.Element => {
	const [fallbackWidth, setFallbackWidth] = useState(defaultWidth ?? fallbackDefaultWidth);
	const onLegacyResizeEnd = useCallback<NonNullable<PanelProps['onResizeEnd']>>(
		({ finalWidth }) => onResizeEnd?.(finalWidth),
		[onResizeEnd],
	);
	return fg('platform-dst-chat-panel-layout') ? (
		<ChatLayoutPanelSlot
			width={width}
			onWidthChange={onWidthChange}
			defaultWidth={defaultWidth}
			compactDefaultWidth={compactDefaultWidth}
			minWidth={minWidth}
			maxWidth={maxWidth}
			initialInlineWidth={initialInlineWidth}
			label={label}
			testId={testId}
			onResizeEnd={onResizeEnd}
			onInlineResizeEnd={onInlineResizeEnd}
			layoutRef={layoutRef}
			managedSizing={managedSizing}
			getManagedResizeBounds={getManagedResizeBounds}
			onManagedResizeStart={onManagedResizeStart}
			onManagedResize={onManagedResize}
			onManagedResizeEnd={onManagedResizeEnd}
		>
			{children}
		</ChatLayoutPanelSlot>
	) : (
		<LegacyPanelSlot
			contentKey={contentKey}
			defaultWidth={defaultWidth}
			width={width ?? fallbackWidth}
			label={label}
			testId={testId}
			onWidthChange={onWidthChange ?? onInlineResizeEnd ?? setFallbackWidth}
			onResizeEnd={onResizeEnd ? onLegacyResizeEnd : undefined}
		>
			{children}
		</LegacyPanelSlot>
	);
};
